import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Button,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PartLineRow from './PartLineRow';
import AddPartsModal from './AddPartsModal';
import { money, partsTotals, catalogPartInScope } from './partsUtils';

const PartsPanel = ({
  vehicleId,
  parts,
  scope,
  mpiWorkItemId,
  workItemId,
  appraisalId,
  title = 'Parts',
  showTotals = false,
  showFiles = false,
  vehicleSubdomain,
  locked = false,
  children,
}) => {
  const catalog_parts = useSelector(state => state.settings_refresh?.catalog_parts || []);
  const active_vehicle = useSelector(state => state.settings_refresh?.active_vehicle || {});
  const dealership = useSelector(state => state.settings_dealerships?.installation?.subdomain || state.settings_dealerships?.current_dealership || '');
  const currentUser = useSelector(state => state.current_user || {});
  const currentUserProfile = useSelector(state => state.settings_users?.current_user_profile || {});
  const uid = parseInt(currentUser.user, 10) || currentUserProfile?.id || null;

  const scopeSubdomain = vehicleSubdomain || active_vehicle?.subdomain || '';

  const catalogOptions = useMemo(
    () => catalog_parts.filter(p => !p.deleted
      && (p.active === true || p.active === 'true')
      && catalogPartInScope(p, scopeSubdomain)),
    [catalog_parts, scopeSubdomain],
  );

  const totals = useMemo(() => partsTotals(parts), [parts]);

  const basePayload = useMemo(() => {
    const b = { vehicle_id: vehicleId, work_item_source: scope, dealership };
    if (scope === 'new_work_items') b.mpi_work_item_id = mpiWorkItemId;
    if (scope === 'legacy_work_items') b.work_item_id = workItemId;
    if (scope === 'appraisals') b.appraisal_id = appraisalId;
    return b;
  }, [scope, vehicleId, mpiWorkItemId, workItemId, appraisalId, dealership]);

  const [addOpen, setAddOpen] = useState(false);

  return (
    <Box sx={{
      width: '100%', mb: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1,
    }}
    >
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1,
      }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="subtitle2">{title}</Typography>
          {!locked && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
              Add Part
            </Button>
          )}
        </Box>
        {showTotals && totals.lineCount > 0 && (
          <Typography variant="body2">
            <strong>Total:</strong> ${money(totals.total)}
            {totals.declinedCount > 0 ? ` (${totals.declinedCount} declined excluded)` : ''}
          </Typography>
        )}
      </Box>

      {showTotals && totals.lineCount > 0 && (
        <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
          Approved ${money(totals.approvedTotal)} · Pending ${money(totals.pendingTotal)}
        </Typography>
      )}

      {children ? <Box sx={{ mb: 1.5 }}>{children}</Box> : null}

      {parts.length > 0 && (
        <Table size="small" sx={{ mb: 1.5, tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              {showFiles && <TableCell sx={{ width: 80 }}>Files</TableCell>}
              <TableCell sx={{ minWidth: 150 }}>Part #</TableCell>
              <TableCell sx={{ width: '40%', minWidth: 320 }}>Description</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Availability</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parts.map(part => (
              <PartLineRow
                key={part.id}
                part={part}
                vehicleId={vehicleId}
                uid={uid}
                showFiles={showFiles}
                columnCount={showFiles ? 9 : 8}
                locked={locked}
              />
            ))}
          </TableBody>
          {showTotals && totals.lineCount > 0 && (
            <TableBody>
              <TableRow>
                <TableCell colSpan={showFiles ? 5 : 4} align="right"><strong>Parts Total</strong></TableCell>
                <TableCell align="right"><strong>${money(totals.total)}</strong></TableCell>
                <TableCell colSpan={3} />
              </TableRow>
            </TableBody>
          )}
        </Table>
      )}

      <AddPartsModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        catalogOptions={catalogOptions}
        basePayload={basePayload}
        vehicleId={vehicleId}
      />
    </Box>
  );
};

export default PartsPanel;
